import React, { useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import debounce from "lodash/debounce";

import { WBP_TAB_TITLES } from "../WatchListBanPnlTab/constants";
import { PNL_TABLE_COLUMNS } from "./constants";
import { complianceSearchActions } from "../../../../reducers/complianceSearch";
import { FilterAutocomplete, Table } from "../../../../components/ui";
import { useComplianceSearchContext } from "../../../../context";
import { COMPLIANCE_API_URLS, ComplianceAPI } from "../../../../api/compliance";
import { appendSearchParamsToQuery, TitleUpdated } from "../../utils";
import { SectionTitle } from "../../styledComponents";

const PnLCalculator: React.FC = () => {
  const dispatch = useDispatch();

  const { tabsTablesSearchState, setTabsTablesSearchState } = useComplianceSearchContext();
  const { search } = tabsTablesSearchState.watchListBanPnlTab.pnLCalculatorTable;

  const { data, loading } = useSelector((state) => state.complianceSearch.pnlCounter);

  const debouncedSearch = useCallback(
    debounce((values) => {
      setTabsTablesSearchState({
        ...tabsTablesSearchState,
        watchListBanPnlTab: {
          ...tabsTablesSearchState.watchListBanPnlTab,
          pnLCalculatorTable: {
            search: values,
          },
        },
      });
    }, 1000),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const { isError, error } = useQuery({
    retry: false,
    enabled: !!search,
    queryKey: [COMPLIANCE_API_URLS.getPnlData, search],
    async queryFn({ signal }) {
      dispatch(complianceSearchActions.fetchPnLCounterRequest());
      const query = new URLSearchParams();
      appendSearchParamsToQuery(search, query);
      const response = await ComplianceAPI.getPnlDataRequest(query.toString(), signal);
      dispatch(complianceSearchActions.fetchPnLCounterSuccess(response.data));
      return response;
    },
  });

  useEffect(() => {
    if (isError && error) {
      console.error("Error fetching pnl data", error);
      dispatch(complianceSearchActions.fetchPnLCounterFailed());
    }
  }, [isError, error]);

  return (
    <SectionTitle title="PNL counter">
      <div>
        <FilterAutocomplete
          search={search}
          setSearch={debouncedSearch}
          filters={{
            EntriesId: "compliance_entries_id",
            Name: "names",
            Email: "email",
          }}
        />
      </div>

      <Table
        title={<TitleUpdated value={data?.updated} />}
        columns={PNL_TABLE_COLUMNS}
        loading={loading}
        extractKey={(row) => row.compliance_entries_id}
        data={data.pnlData}
        csv_table_name={WBP_TAB_TITLES.pnlTable}
      />
    </SectionTitle>
  );
};

export default PnLCalculator;
